import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { searchComuni, canonComune } from "../lib/comuni";
import { Input } from "./ui/input";
import { Loader2 } from "lucide-react";

// Campo di input predittivo (autocomplete) alimentato dagli endpoint /lookup.
// props:
//  - value, onChange(value)
//  - source: comuni | province | marche | modelli | compagnie
//  - extraParams: { marca } (per modelli)
//  - onSelectItem(item): callback opzionale con l'oggetto completo scelto
export default function AutocompleteInput({
  value, onChange, source, extraParams = {}, onSelectItem, placeholder, testId, className = "", disabled,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const timer = useRef(null);
  const skipNext = useRef(false);
  const focused = useRef(false);

  const label = (it) => {
    if (source === "comuni") return `${it.nome} (${it.provincia})`;
    if (source === "province") return `${it.nome} (${it.sigla})`;
    return it.value;
  };
  const toValue = (it) => {
    if (source === "comuni") return it.nome;
    if (source === "province") return it.sigla;
    return it.value;
  };

  const runSearch = async (q) => {
    if (!q || q.length < (source === "comuni" ? 2 : 1)) { setItems([]); setOpen(false); return; }
    setLoading(true);
    try {
      let result;
      if (source === "comuni") {
        result = searchComuni(q, 20);
      } else {
        const params = new URLSearchParams({ q, ...extraParams }).toString();
        const { data } = await api.get(`/lookup/${source}?${params}`);
        result = data;
      }
      setItems(result);
      if (focused.current) setOpen(result.length > 0);
      setActive(-1);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Ricerca al cambio del valore SOLO se il campo è focalizzato dall'utente:
  // evita l'apertura automatica del dropdown al mount con valore precompilato.
  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    if (!focused.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => runSearch(value || ""), 200);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, source, extraParams.marca]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (it) => {
    skipNext.current = true;
    onChange(toValue(it));
    onSelectItem?.(it);
    setOpen(false);
    setItems([]);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKey}
        onFocus={() => { focused.current = true; runSearch(value || ""); }}
        onBlur={() => {
          focused.current = false;
          if (source === "comuni") { const c = canonComune(value); if (c && c !== value) onChange(c); }
        }}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        autoComplete="off"
        className={`bg-slate-900/60 border-slate-700 ${className}`}
      />
      {loading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-slate-500" />}
      {open && items.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-700 bg-[#0d111a] shadow-xl" data-testid={`${testId}-list`}>
          {items.map((it, i) => (
            <button
              type="button"
              key={i}
              onMouseDown={(e) => { e.preventDefault(); choose(it); }}
              className={`w-full text-left px-3 py-2 text-sm ${i === active ? "bg-amber-500/15 text-amber-300" : "text-slate-200 hover:bg-slate-800/70"}`}
            >
              {label(it)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
